import { useEffect, type CSSProperties } from 'react';
import type { Settings } from '@core/settings/schema.ts';
import {
  STYLE_PRESETS,
  trackStyleFromSettings,
  type TrackStyle,
} from '@core/styles/stylePresets.ts';
import { styleToCss, syncGoogleFontLink } from '../core/styleToCss.ts';
import { useI18n } from '../i18n/index.ts';

const PREVIEW_VIEW_HEIGHT = 360;
const THUMB_VIEW_HEIGHT = 220;

const DEFAULT_PREVIEW = STYLE_PRESETS[0]?.preview ?? {
  main: 'Hello, how are you?',
  second: '你好，你好嗎？',
  thumb: 'Aa',
};

function lineCss(style: TrackStyle, viewHeight: number): CSSProperties {
  return {
    ...styleToCss(style, { viewHeight }),
    display: 'inline-block',
    margin: '0.2rem 0',
    borderRadius: '0.35rem',
  };
}

export type StyleLookPreviewProps = {
  settings: Settings;
  previewMain?: string;
  previewSecond?: string;
};

/** Dark dual-line live preview of main + second track styles (Style MVP). */
export function StyleLookPreview({
  settings,
  previewMain,
  previewSecond,
}: StyleLookPreviewProps) {
  const { t } = useI18n();
  const mainStyle = trackStyleFromSettings(settings, 'main');
  const secondStyle = trackStyleFromSettings(settings, 'second');
  const mainCss = lineCss(mainStyle, PREVIEW_VIEW_HEIGHT);
  const secondCss = lineCss(secondStyle, PREVIEW_VIEW_HEIGHT);
  const mainText = previewMain ?? DEFAULT_PREVIEW.main;
  const secondText = previewSecond ?? DEFAULT_PREVIEW.second;

  useEffect(() => {
    syncGoogleFontLink('main', mainStyle.fontGoogleFamilies, mainStyle.fontGoogleCssProperties);
    syncGoogleFontLink('second', secondStyle.fontGoogleFamilies, secondStyle.fontGoogleCssProperties);
  }, [
    mainStyle.fontGoogleFamilies,
    mainStyle.fontGoogleCssProperties,
    secondStyle.fontGoogleFamilies,
    secondStyle.fontGoogleCssProperties,
  ]);

  return (
    <div className="style-look-preview" data-testid="style-look-preview">
      <p className="style-look-preview__hint">{t('settingsStylePreviewHint')}</p>
      <div className="style-look-preview__stage" data-testid="style-look-stage">
        <div className="style-look-preview__inner">
          <span
            className={`style-look-preview__line${mainStyle.fontGoogleFamilies ? ' rikosub-google-font-main' : ''}`}
            style={mainCss}
            data-testid="style-look-main"
          >
            {mainText}
          </span>
          <span
            className={`style-look-preview__line${secondStyle.fontGoogleFamilies ? ' rikosub-google-font-second' : ''}`}
            style={secondCss}
            data-testid="style-look-second"
          >
            {secondText}
          </span>
        </div>
      </div>
    </div>
  );
}

export type StylePresetThumbProps = {
  style: TrackStyle;
  sample: string;
  testId?: string;
};

export function StylePresetThumb({ style, sample, testId }: StylePresetThumbProps) {
  return (
    <div className="settings-panel__preset-thumb-stage" data-testid={testId}>
      <span style={lineCss(style, THUMB_VIEW_HEIGHT)}>{sample}</span>
    </div>
  );
}
